import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface AuthState {
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  epicSession: {
    state: string | null
    accessToken: string | null
    fhirPatientId: string | null
    fhirUserId: string | null
    fhirServer: string | null
  }
  user: {
    id: string | null
    email: string | null
    name: string | null
  } | null
}

const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: false,
  error: null,
  epicSession: {
    state: null,
    accessToken: null,
    fhirPatientId: null,
    fhirUserId: null,
    fhirServer: null,
  },
  user: null,
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Authentication state
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    setAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload
    },

    // Epic session management
    setEpicSession: (
      state,
      action: PayloadAction<{
        state: string
        accessToken: string
        fhirPatientId: string
        fhirUserId: string
        fhirServer: string
      }>
    ) => {
      const { state: sessionState, accessToken, fhirPatientId, fhirUserId, fhirServer } = action.payload
      state.epicSession = {
        state: sessionState,
        accessToken,
        fhirPatientId,
        fhirUserId,
        fhirServer,
      }
      state.isAuthenticated = true
    },
    clearEpicSession: (state) => {
      state.epicSession = {
        state: null,
        accessToken: null,
        fhirPatientId: null,
        fhirUserId: null,
        fhirServer: null,
      }
    },

    // User information
    setUser: (
      state,
      action: PayloadAction<{
        id: string
        email: string
        name: string
      }>
    ) => {
      state.user = action.payload
    },
    clearUser: (state) => {
      state.user = null
    },

    // Logout
    logout: (state) => {
      state.isAuthenticated = false
      state.user = null
      state.epicSession = {
        state: null,
        accessToken: null,
        fhirPatientId: null,
        fhirUserId: null,
        fhirServer: null,
      }
      state.error = null
    },
  },
})

export const { setLoading, setError, setAuthenticated, setEpicSession, clearEpicSession, setUser, clearUser, logout } =
  authSlice.actions

export default authSlice.reducer
